import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AiPanel } from "../components/dashboard/AiPanel";
import { WeatherCard } from "../components/dashboard/WeatherCard";
import {
  MyDayCard, MeetingCard, WaitingCard, TasksCard, MessagesCard,
  DeadlinesCard, TeamTodayCard, MyRequestsCard, TrainingCard,
  ChecklistCard, ActivityCard, CardSkeleton,
} from "../components/dashboard/widgets";

const LIVE_POLL_MS = 60000;

const greetingNow = () => {
  const h = new Date().getHours();
  if (h < 12) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
};

const fmtDayLong = () => {
  const s = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/* Cella della griglia: col = colonne desktop (12-col grid), order = posizione mobile.
   Classi statiche (mappe) perché Tailwind JIT non genera classi dinamiche. */
const COL_CLASS = { 4: "md:col-span-4", 8: "md:col-span-8", 12: "md:col-span-12" };
const ORDER_CLASS = {
  1: "order-1", 2: "order-2", 3: "order-3", 4: "order-4", 5: "order-5",
  6: "order-6", 7: "order-7", 8: "order-8", 9: "order-9", 10: "order-10", 11: "order-11",
};
const Cell = ({ col = 4, order, children }) => (
  <div className={`${COL_CLASS[col]} ${ORDER_CLASS[order]} md:order-none min-w-0`}>{children}</div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    setError(false);
    api.get("/dashboard/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const refreshLive = useCallback(() => {
    api.get("/dashboard/summary", { params: { live: 1 } })
      .then(({ data }) => setSummary((prev) => (prev ? { ...prev, ...data } : prev)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(refreshLive, LIVE_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [load, refreshLive]);

  const isAdmin = summary?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";
  const presentCount = summary?.team_today?.counts?.present;

  if (loading) {
    return (
      <div className="wl-page" data-testid="dashboard-v2-loading">
        <div>
          <h1 className="wl-page__greeting">{greetingNow()}…</h1>
          <p className="wl-page__meta">{fmtDayLong()}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <div className="md:col-span-8"><CardSkeleton tall /></div>
          <div className="md:col-span-4"><CardSkeleton tall /></div>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="md:col-span-4"><CardSkeleton /></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="wl-page" data-testid="dashboard-v2-error">
        <div>
          <h1 className="wl-page__greeting">{greetingNow()}</h1>
          <p className="wl-page__meta">{fmtDayLong()}</p>
        </div>
        <div className="bg-surface border border-border rounded-[10px] p-6 text-center">
          <div className="text-sm mb-3" style={{ color: "var(--wl-text-secondary)" }}>
            Non riusciamo a caricare la dashboard in questo momento.
          </div>
          <button type="button" onClick={() => { setLoading(true); load(); }}
                  className="wl-btn wl-btn--primary wl-btn--sm" data-testid="dashboard-retry">
            Riprova
          </button>
        </div>
      </div>
    );
  }

  const has = (key) => key in summary;
  const showMyDay = has("attendance") && !user?.attendance_exempt && !isSuperAdmin;

  return (
    <div className="wl-page" data-testid="dashboard-v2">
      {/* Saluto */}
      <div data-testid="dashboard-greeting">
        <h1 className="wl-page__greeting">
          {greetingNow()}, <span className="wl-page__greeting-name">{summary.user_name}</span>
        </h1>
        <p className="wl-page__meta">
          {fmtDayLong()}
          {summary.org_name && <> · <strong>{summary.org_name}</strong></>}
          {isAdmin && typeof presentCount === "number" && (
            <> · {presentCount} {presentCount === 1 ? "persona" : "persone"} in servizio</>
          )}
        </p>
      </div>

      {/* Griglia 12 colonne desktop / 1 colonna mobile (order-* = sequenza mobile) */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {has("ai") && (
          <Cell col={8} order={3}><AiPanel ai={summary.ai} isAdmin={isAdmin} /></Cell>
        )}
        {has("weather") && (
          <Cell col={4} order={1}>
            <WeatherCard weather={summary.weather} isAdmin={isAdmin} onCitySaved={load} />
          </Cell>
        )}
        {showMyDay && (
          <Cell col={4} order={2}><MyDayCard data={summary.attendance} onChanged={refreshLive} canClockMobile={!!user?.can_clock_mobile} /></Cell>
        )}
        {has("meeting") && (
          <Cell col={4} order={4}><MeetingCard meeting={summary.meeting} /></Cell>
        )}
        {has("waiting") && (
          <Cell col={4} order={5}><WaitingCard waiting={summary.waiting} /></Cell>
        )}
        {has("tasks") && (
          <Cell col={4} order={5}><TasksCard tasks={summary.tasks} /></Cell>
        )}
        {has("messages") && (
          <Cell col={4} order={6}><MessagesCard messages={summary.messages} /></Cell>
        )}
        {has("deadlines") && (
          <Cell col={4} order={7}><DeadlinesCard deadlines={summary.deadlines} /></Cell>
        )}
        {has("team_today") && (
          <Cell col={4} order={8}><TeamTodayCard team={summary.team_today} /></Cell>
        )}
        {has("my_requests") && (
          <Cell col={4} order={8}><MyRequestsCard data={summary.my_requests} /></Cell>
        )}
        {has("training") && (
          <Cell col={4} order={9}><TrainingCard training={summary.training} /></Cell>
        )}
        {has("checklist") && (
          <Cell col={4} order={10}><ChecklistCard checklist={summary.checklist} /></Cell>
        )}
        {has("activity") && (
          <Cell col={4} order={11}><ActivityCard items={summary.activity} isAdmin={isAdmin} /></Cell>
        )}
      </div>
    </div>
  );
}
